import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScheduleLinkType, ScheduleTemplateItem } from '@/lib/scheduleUtils';
import ScheduleTemplateLagInput from './ScheduleTemplateLagInput';
import type { EditableTemplateLink } from './ScheduleTemplateEditorLinksSection';
import { computeTemplateTimeline } from './scheduleTemplateTimeline';

const DAY_WIDTH = 28;
const ROW_HEIGHT = 36;
const LABEL_WIDTH = 220;

interface Props {
  items: ScheduleTemplateItem[];
  links: EditableTemplateLink[];
  onChange: (links: EditableTemplateLink[]) => void;
  disabled?: boolean;
  itemById: Map<string, ScheduleTemplateItem>;
  onOpenAddLink: (prefill?: { predecessorId?: string; successorId?: string }) => void;
}

export default function ScheduleTemplateGanttView({
  items,
  links,
  onChange,
  disabled,
  itemById,
  onOpenAddLink,
}: Props) {
  const { t } = useTranslation();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const { bars, totalDays } = useMemo(
    () => computeTemplateTimeline(items, links),
    [items, links]
  );

  const selectedTask = selectedTaskId ? itemById.get(selectedTaskId) : null;

  const incomingLinks = useMemo(
    () => (selectedTaskId ? links.filter((l) => l.successor_item_id === selectedTaskId) : []),
    [links, selectedTaskId]
  );

  const outgoingLinks = useMemo(
    () => (selectedTaskId ? links.filter((l) => l.predecessor_item_id === selectedTaskId) : []),
    [links, selectedTaskId]
  );

  const handleUpdateLag = (localId: string, lag: number, type: ScheduleLinkType) => {
    onChange(
      links.map((l) =>
        l._localId === localId ? { ...l, lag_days: lag, link_type: type } : l
      )
    );
  };

  const handleRemove = (localId: string) => {
    onChange(links.filter((l) => l._localId !== localId));
  };

  const taskLabel = (itemId: string) => {
    const item = itemById.get(itemId);
    if (!item) return t('templates.unknownTask');
    return `${item.sort_order}. ${item.title || t('templates.untitledTask')}`;
  };

  const gridWidth = Math.max(totalDays, 1) * DAY_WIDTH;

  if (bars.length === 0) {
    return (
      <p className="text-sm text-[#9CA3AF] py-6 text-center">{t('templates.noItems')}</p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-[#6B7280]">{t('templates.ganttSyntheticHint')}</p>

      <div className="border border-gray-100 rounded-xl overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <div className="min-w-max">
            <div className="flex border-b border-gray-100 bg-[#F9FAFB]">
              <div
                className="flex-shrink-0 px-3 py-2 text-[10px] font-bold text-[#9CA3AF] uppercase border-r border-gray-100"
                style={{ width: LABEL_WIDTH }}
              >
                {t('templates.taskTitle')}
              </div>
              <div className="flex" style={{ width: gridWidth }}>
                {Array.from({ length: totalDays }, (_, i) => i + 1).map((day) => (
                  <div
                    key={day}
                    className="flex-shrink-0 text-center text-[9px] font-semibold text-[#9CA3AF] py-2 border-r border-gray-50"
                    style={{ width: DAY_WIDTH }}
                  >
                    D{day}
                  </div>
                ))}
              </div>
            </div>

            <div className="max-h-[360px] overflow-y-auto">
              {bars.map(({ item, startDay, duration, endDay }) => {
                if (!item.id) return null;
                const isSelected = selectedTaskId === item.id;
                const predCount = links.filter((l) => l.successor_item_id === item.id).length;
                const succCount = links.filter((l) => l.predecessor_item_id === item.id).length;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedTaskId(item.id)}
                    className={`flex w-full text-left border-b border-gray-50 cursor-pointer transition-colors ${
                      isSelected ? 'bg-[#FEF0E7]/60' : 'hover:bg-gray-50/80'
                    }`}
                    style={{ minHeight: ROW_HEIGHT }}
                  >
                    <div
                      className="flex-shrink-0 px-3 py-2 border-r border-gray-100 flex flex-col justify-center min-w-0"
                      style={{ width: LABEL_WIDTH }}
                    >
                      <p className="text-xs font-semibold text-[#1A1A1A] truncate">
                        {item.sort_order}. {item.title || t('templates.untitledTask')}
                      </p>
                      <p className="text-[10px] text-[#9CA3AF] truncate">
                        {duration} {t('templates.days')}
                        {(predCount > 0 || succCount > 0) && (
                          <span className="ml-1">
                            · {predCount}/{succCount} {t('templates.ganttLinkCounts')}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="relative flex-shrink-0" style={{ width: gridWidth, height: ROW_HEIGHT }}>
                      <div
                        className="absolute top-1/2 -translate-y-1/2 h-5 rounded-md bg-[#E8621A]/90 border border-[#E8621A] shadow-sm flex items-center px-1.5 overflow-hidden"
                        style={{
                          left: (startDay - 1) * DAY_WIDTH + 2,
                          width: Math.max(duration * DAY_WIDTH - 4, DAY_WIDTH - 4),
                        }}
                        title={`Day ${startDay}–${endDay}`}
                      >
                        <span className="text-[9px] font-bold text-white truncate">
                          D{startDay}–{endDay}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="border border-gray-100 rounded-xl bg-[#F9FAFB] p-3 space-y-3">
        {!selectedTask ? (
          <p className="text-sm text-[#9CA3AF] text-center py-4">{t('templates.ganttSelectTask')}</p>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <p className="text-xs font-bold text-[#374151] uppercase tracking-wider">
                  {t('templates.ganttSelectedTask')}
                </p>
                <p className="text-sm font-semibold text-[#1A1A1A] mt-0.5">
                  {selectedTask.sort_order}. {selectedTask.title || t('templates.untitledTask')}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onOpenAddLink({ predecessorId: selectedTask.id })}
                  className="px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-semibold text-[#374151] cursor-pointer hover:border-[#E8621A]"
                >
                  {t('templates.ganttAddSuccessor')}
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onOpenAddLink({ successorId: selectedTask.id })}
                  className="px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-semibold text-[#374151] cursor-pointer hover:border-[#E8621A]"
                >
                  {t('templates.ganttAddPredecessor')}
                </button>
              </div>
            </div>

            <LinkGroup
              title={t('templates.predecessorLinks')}
              emptyLabel={t('templates.noPredecessorLinks')}
              linkRows={incomingLinks.map((link) => ({
                link,
                otherId: link.predecessor_item_id,
                direction: 'from' as const,
              }))}
              taskLabel={taskLabel}
              disabled={disabled}
              onUpdateLag={handleUpdateLag}
              onRemove={handleRemove}
              t={t}
            />

            <LinkGroup
              title={t('templates.successorLinks')}
              emptyLabel={t('templates.noSuccessorLinks')}
              linkRows={outgoingLinks.map((link) => ({
                link,
                otherId: link.successor_item_id,
                direction: 'to' as const,
              }))}
              taskLabel={taskLabel}
              disabled={disabled}
              onUpdateLag={handleUpdateLag}
              onRemove={handleRemove}
              t={t}
            />
          </>
        )}
      </div>
    </div>
  );
}

function LinkGroup({
  title,
  emptyLabel,
  linkRows,
  taskLabel,
  disabled,
  onUpdateLag,
  onRemove,
  t,
}: {
  title: string;
  emptyLabel: string;
  linkRows: Array<{
    link: EditableTemplateLink;
    otherId: string;
    direction: 'from' | 'to';
  }>;
  taskLabel: (id: string) => string;
  disabled?: boolean;
  onUpdateLag: (localId: string, lag: number, type: ScheduleLinkType) => void;
  onRemove: (localId: string) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <div>
      <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1.5">{title}</p>
      {linkRows.length === 0 ? (
        <p className="text-xs text-[#9CA3AF] py-1">{emptyLabel}</p>
      ) : (
        <div className="space-y-1.5">
          {linkRows.map(({ link, otherId, direction }) => (
            <div
              key={link._localId}
              className="flex flex-col sm:flex-row sm:items-center gap-2 bg-white rounded-lg border border-gray-100 px-3 py-2"
            >
              <div className="flex-1 min-w-0 text-xs text-[#374151]">
                <span className="text-[#9CA3AF] mr-1">{direction === 'from' ? '←' : '→'}</span>
                <span className="font-semibold">{taskLabel(otherId)}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] font-bold text-[#9CA3AF] uppercase hidden sm:inline">
                  {t('templates.linkTypeLabel')}
                </span>
                <select
                  value={link.link_type}
                  disabled={disabled}
                  onChange={(e) =>
                    onUpdateLag(link._localId, link.lag_days, e.target.value as ScheduleLinkType)
                  }
                  className="px-2 py-1.5 rounded-lg border border-gray-200 text-xs min-w-[4rem]"
                >
                  <option value="FS">FS</option>
                  <option value="SS">SS</option>
                </select>
                <span className="text-[10px] font-bold text-[#9CA3AF] uppercase hidden sm:inline">
                  {t('templates.lagLabel')}
                </span>
                <ScheduleTemplateLagInput
                  value={link.lag_days}
                  disabled={disabled}
                  onChange={(lag) => onUpdateLag(link._localId, lag, link.link_type)}
                  title={t('templates.lagDays')}
                />
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onRemove(link._localId)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-500 cursor-pointer"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
